import React, { useCallback, useRef } from 'react';
import { publishElementsDataType } from '../../Pages/Flo/Flo.types';
import { get } from 'lodash';
import classNames from 'classnames';
import styles from './VideoPlayer.module.css';
import AdventureCtaBody, {
	AdventureValuesType,
} from './AdventureCtaBody/AdventureCtaBody';
import { trackCompletionAction } from '../../Pages/Flo/Flo.reducers';
import { useDispatch } from 'react-redux';

export const getAdventureCtaDialogProperties = (
	ctaElement: publishElementsDataType | undefined,
	floId: string,
	floIndex: number
) => {
	const isAdventureCta = get(ctaElement, 'subElementName') === 'interaction_cues';
	return {
		classContainer: classNames(styles.classContainer, {
			[styles.adventureCtaContainer]: isAdventureCta,
		}),
		body: isAdventureCta ? (
			<AdventureCtaBody
				values={get(ctaElement, 'values') as AdventureValuesType}
				id={get(ctaElement, 'id') || get(ctaElement, 'editorElementId', '')}
				floEmbedUrls={get(ctaElement, 'floEmbedUrls')}
				floId={floId}
				floIndex={floIndex}
			/>
		) : (
			get(ctaElement, 'values.subheading', '') || ''
		),
		showConfirm: !isAdventureCta,
		showCancel: false,
		cancelButtonClassName: styles.adventureCtaCancelButton,
		hideClosebutton: true,
	};
};

export const findTooltipDefaultPosition = (
	canvasCoordinates: { x: number; y: number },
	windowsCoordinates: { width: number; height: number }
) => {
	let positionHorizontal = '';
	let positionVertical = '';

	const percentageWidth = Math.max(windowsCoordinates.width * 0.3);
	const onLeft = canvasCoordinates.x < percentageWidth;
	const onRight = canvasCoordinates.x > windowsCoordinates.width - percentageWidth;

	const percentageHeight = Math.max(windowsCoordinates.height * 0.3);
	const onTop = canvasCoordinates.y < percentageHeight;
	const onButtom = canvasCoordinates.y > windowsCoordinates.height - percentageHeight;
	if (onTop) {
		positionVertical = 'bottom';
		if (onLeft) {
			positionHorizontal = 'start';
		}
		if (onRight) {
			positionHorizontal = 'end';
		}
		return `${positionVertical}${positionHorizontal ? '-' : ''}${positionHorizontal}`;
	} else if (onButtom) {
		positionVertical = 'top';
		if (onLeft) {
			positionHorizontal = 'start';
		}
		if (onRight) {
			positionHorizontal = 'end';
		}
		return `${positionVertical}${positionHorizontal ? '-' : ''}${positionHorizontal}`;
	}

	if (onButtom) {
		return 'top';
	} else if (onRight) {
		return 'left';
	} else if (onTop) {
		return 'bottom';
	} else {
		return 'right';
	}
};

export const useTrackCompletionStatus = ({
	duration,
	floId,
	floIndex,
}: {
	duration: number;
	floId: string;
	floIndex: string;
}) => {
	const lastSeekedTime = useRef<{ percentage: number }>({ percentage: -1 });
	const dispatch = useDispatch();

	const resetLastSeekedTime = useCallback(() => {
		lastSeekedTime.current.percentage = -1;
	}, [lastSeekedTime.current]);

	const sendCompletionStatus = useCallback(
		(val: number) => {
			if (val === 0) {
				return;
			}
			if (lastSeekedTime.current && duration) {
				const percentageComplete = Math.floor((val / duration) * 4) * 25;
				if (percentageComplete !== lastSeekedTime.current.percentage) {
					lastSeekedTime.current.percentage = percentageComplete;
					dispatch(
						trackCompletionAction({
							viewedPercentage: percentageComplete,
							floId,
							floIndex,
						})
					);
				}
			}
		},
		[duration, lastSeekedTime.current]
	);

	return {
		sendCompletionStatus,
		resetLastSeekedTime,
	};
};
